import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommerceActionNotificationVariant } from '@/components/toast/commerce-action-notification';
import {
    commerceNotificationCardClassName,
    commerceNotificationDesktopWidthClassName,
    commerceNotificationDismissLayoutClassName,
    commerceNotificationMessageClassName,
} from '@/components/toast/commerce-notification-styles';
import { DismissCloseButton } from '@/components/ui/dismiss-close-button';
import { cn } from '@/lib/utils';

export type GiftCardRemovedNotificationProps = {
    variant: CommerceActionNotificationVariant;
    maskedLabel: string;
    onDismiss?: () => void;
    className?: string;
};

function GiftCardRemovedNotificationMessage({ maskedLabel }: { maskedLabel: string }) {
    const { t } = useTranslation('account');

    return (
        <p className={commerceNotificationMessageClassName}>
            <strong className="font-bold">{maskedLabel}</strong>
            {t('giftCards.notification.removedMiddle')}
        </p>
    );
}

export function GiftCardRemovedNotification({
    variant,
    maskedLabel,
    onDismiss,
    className,
}: GiftCardRemovedNotificationProps): ReactElement {
    const { t } = useTranslation('account');
    const isDesktop = variant === 'desktop';
    const closeLabel = t('giftCards.closeDrawer');

    if (!isDesktop) {
        return (
            <div role="status" aria-live="polite" className={cn('flex justify-between gap-4', className)}>
                <div className="flex min-w-0 items-start pb-6">
                    <GiftCardRemovedNotificationMessage maskedLabel={maskedLabel} />
                </div>
                {onDismiss ? <DismissCloseButton aria-label={closeLabel} onClick={onDismiss} /> : null}
            </div>
        );
    }

    return (
        <div
            role="status"
            aria-live="polite"
            className={cn(commerceNotificationCardClassName, commerceNotificationDesktopWidthClassName, className)}>
            <GiftCardRemovedNotificationMessage maskedLabel={maskedLabel} />
            {onDismiss ? (
                <DismissCloseButton
                    aria-label={closeLabel}
                    onClick={onDismiss}
                    className={commerceNotificationDismissLayoutClassName}
                />
            ) : null}
        </div>
    );
}
